import { Center, Loader, MantineProvider } from "@mantine/core";
import { Notifications } from "@mantine/notifications";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router";
import { AuthProvider, useAuth } from "./auth/provider";
import { LocaleProvider } from "./i18n/provider";
import { ResetPasswordPage } from "./pages/auth/reset-password";
import { SignInPage } from "./pages/auth/sign-in";
import { SignUpPage } from "./pages/auth/sign-up";
import { VerifyEmailPage } from "./pages/auth/verify-email";
import { BillingPage } from "./pages/billing";
import { DashboardPage } from "./pages/dashboard";
import { EditorPage } from "./pages/editor/editor";
import { EmbedPage } from "./pages/embed";
import { InvitePage } from "./pages/invite";
import { LandingPage } from "./pages/landing";
import { LegalPage } from "./pages/legal";
import { NotFoundPage } from "./pages/not-found";
import { OrganizationPage } from "./pages/organization";
import { OrganizationsPage } from "./pages/organizations";
import { PublicCvPage } from "./pages/public-cv";
import { SettingsPage } from "./pages/settings";
import { TemplatesPage } from "./pages/templates";
import { theme } from "./theme";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <MantineProvider defaultColorScheme="auto" theme={theme}>
        <Notifications position="top-right" />
        <LocaleProvider>
          <BrowserRouter>
            <AuthProvider>
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/login" element={<SignInPage />} />
                <Route path="/signup" element={<SignUpPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/verify-email" element={<VerifyEmailPage />} />

                <Route
                  path="/dashboard"
                  element={
                    <RequireAuth>
                      <DashboardPage />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/resumes/:id/edit"
                  element={
                    <RequireAuth>
                      <EditorPage />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/templates"
                  element={
                    <RequireAuth>
                      <TemplatesPage />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/organization"
                  element={
                    <RequireAuth>
                      <OrganizationsPage />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/organization/:id"
                  element={
                    <RequireAuth>
                      <OrganizationPage />
                    </RequireAuth>
                  }
                />
                <Route path="/invite/:token" element={<InvitePage />} />
                <Route
                  path="/billing"
                  element={
                    <RequireAuth>
                      <BillingPage />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <RequireAuth>
                      <SettingsPage />
                    </RequireAuth>
                  }
                />

                <Route path="/r/:slug" element={<PublicCvPage />} />
                <Route path="/embed/:slug" element={<EmbedPage />} />

                <Route path="/privacy" element={<LegalPage kind="privacy" />} />
                <Route path="/terms" element={<LegalPage kind="terms" />} />
                <Route path="/refunds" element={<LegalPage kind="refunds" />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </AuthProvider>
          </BrowserRouter>
        </LocaleProvider>
      </MantineProvider>
    </QueryClientProvider>
  );
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <Center h="100dvh">
        <Loader size="sm" color="gray" />
      </Center>
    );
  }

  if (!isAuthenticated) {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }

  return children;
}
